import {computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../components/toast/ErrorToast";


export class SelectDeliveryPointFeature {

    constructor(application, id) {
        this._application = application;
        this._deliverySites = [];
        this._deliveryDates = [];
        this._loaded = false;
        this._isDeliveryPointModalOpened = !this._application.wasOrderChosen();
        this.existsSameOrder = false
        this._isDeliveryPointModalOpened = !this._application.wasOrderChosen() && this.canAddProduct();
        this._isModalCantEditOrderOpened = false

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _deliverySites: observable,
            deliverySites: computed,
            _deliveryDates: observable,
            deliveryDates: computed,
            _isDeliveryPointModalOpened: observable,
            isDeliveryPointModalOpened: computed,
            _isModalCantEditOrderOpened: observable,
            isModalCantEditOrderOpened: computed,
        })
    }

    async load() {
        await this.getDeliverySites()
        this._finishedLoading()
    }

    _finishedLoading() {
        this._loaded = true;
    }

    get isDeliveryPointModalOpened() {
        return this._isDeliveryPointModalOpened;
    }

    set isDeliveryPointModalOpened(value) {
        this._isDeliveryPointModalOpened = value;
    }

    wasDeliverySiteAndDeliveryDateChosen() {
        return this._application.wasOrderChosen()
    }

    get loaded() {
        return this._loaded;
    }

    set loaded(value) {
        this._loaded = value;
    }

    get deliverySites() {
        return this._deliverySites;
    }

    set deliverySites(value) {
        this._deliverySites = value;
    }

    get deliveryDates() {
        return this._deliveryDates;
    }

    set deliveryDates(value) {
        this._deliveryDates = value;
    }

    get isModalCantEditOrderOpened() {
        return this._isModalCantEditOrderOpened;
    }

    set isModalCantEditOrderOpened(value) {
        this._isModalCantEditOrderOpened = value;
    }

    openDeliveryPointModal() {
        this._isDeliveryPointModalOpened = true
    }

    closeDeliveryPointModal() {
        this._isDeliveryPointModalOpened = false
    }

    getDeliverySites() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._deliverySites = response.deliverySites()
            },
            handlesError: (response) => {
                // response.errors().map(error => {
                //     this.showAlert(error);
                // })
                console.log("Error getting delivery sites")
            }
        });

        return this._application.apiClient().usersDeliverySites(responseHandler, this._application.getCustomer());
    }

    updateDeliveryDatesFor(deliverySiteName) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._deliveryDates = response.enabledDates()
            },
            handlesError: (response) => {
                // response.errors().map(error => {
                //     this.showAlert(error);
                // })
                console.log("Error getting delivery sites")
            }
        });

        let deliverySite;
        for (deliverySite of this._deliverySites) {
            if (deliverySite.deliverySiteName === deliverySiteName) {
                break;
            }
        }

        return this._application.apiClient().getEnabledDatesForDeliverySite(
            deliverySite.deliverySiteCode,
            deliverySite.customer.businessName,
            responseHandler);
    }

    isDateValidForDelivery(aDateTime) {
        return this.deliveryDates.some(date => {
            const enabledDate = this.formatDate(date)
            const calendarDate = this.formatDate(new Date(aDateTime))
            return enabledDate === calendarDate
        });
    }

    formatDate(date) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    async setDelivery(deliveryPointName, deliveryDate, purchaseOrderNumber) {
        await this.setDeliverySiteAndDate(deliveryPointName, deliveryDate, purchaseOrderNumber)
        if (!(this._application.order === null)) {
            await this.editOrdersPurchaseOrderNumber(purchaseOrderNumber)
        }
    }

    async editOrdersPurchaseOrderNumber(purchaseOrdersNumbers) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._application.setOrder(response.order())
            },
            handlesError: async (response) => {
                await this.handleOrderEditionError(response);
            }
        });
        this._application.apiClient().editOrdersPurchaseOrderNumber(this._application.order.number, purchaseOrdersNumbers, responseHandler);
    }

    async handleOrderEditionError(response) {
        if (response.editionFailed?.()) {
            this._isModalCantEditOrderOpened = true
        } else if (response.orderWasDeleted?.()) {
            await this._application.session().logOutAndRemoveOrder()
            return errorToast("El pedido fue eliminado. Por favor, ingrese nuevamente y acceda a 'Mis Pedidos' para ver sus pedidos activos.")
        } else {
            return errorToast("No se pudo editar el pedido.");
        }
    }

    async getOrder() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._application.setOrder(response.order())
            },
            handlesError: async (response) => {
                if (response.orderWasDeleted?.()) {
                    await this._application.session().logOutAndRemoveOrder()
                    return errorToast("El pedido fue eliminado. Por favor, ingrese nuevamente y acceda a 'Mis Pedidos' para ver sus pedidos activos.")
                }
            }
        });
        return this._application.apiClient().orderDetail(this._application.getOrder().number, responseHandler);
    }

    async setDeliverySiteAndDate(deliveryPointName, deliveryDate, purchaseOrderNumber) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this.setOrder(response.order());
            },
            handlesError: async (response) => {
                await this.handleOrderEditionError(response);
            }
        });

        const deliverySite = this.getDeliverySiteWithName(deliveryPointName);
        const deliveryDateString = `${deliveryDate.date()}-${deliveryDate.month()+1}-${deliveryDate.year()}`;
        const order = {
            customer: {business_name: this.getBusinessName()},
            delivery_site: {
                name: deliverySite.deliverySiteName,
                address: deliverySite.deliverySiteAddress,
                city: deliverySite.deliverySiteCity,
            },
            // delivery_date: deliveryDate
            delivery_date: deliveryDateString,
            customers_order_number: purchaseOrderNumber
        };
        if (!this.existsAnOrderForSelectedDate() && this._application.wasOrderChosen()) {
            return this._application.apiClient().editOrder(this._application.order.number, order, responseHandler);
        } else {
            return this._application.apiClient().createOrder(order, responseHandler);
        }
    }

    setOrder(order) {
        this._application.setOrder(order);
    }

    getBusinessName() {
        return this._deliverySites[0].customer.businessName;
    }

    getDeliverySiteWithName(deliveryPointName) {
        return this._deliverySites.find((deliverySite) => deliverySite.deliverySiteName === deliveryPointName)
    }

    existsAnOrderForSelectedDate() {
        return this.existsSameOrder
    }

    getDeliverySiteName(){
        if (this._application.order !== null){
            return this._application.order.deliverySite.deliverySiteName
        }
    }

    getDeliveryDate(){
        if (this._application.order !== null){
            return this._application.order.deliveryDate
        }
    }

    purchaseOrderNumber(){
        if (this._application.order !== null){
            return this._application.order.customersOrderNumber
        }
    }

    isSameOrder(orderNumbers) {
        if (this._application.order !== null) {
            return (orderNumbers.includes(this._application.order.number)) && (orderNumbers.length === 1)
        }
        return false
    }

    removeOrder() {
        this._application.removeOrder()
    }

    existsSameOrderWith(deliveryDate, deliverySiteString, purchaseOrderNumber) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                if (this.isSameOrder(response.orderNumbers())) {
                    this.existsSameOrder = false
                } else {
                    this.existsSameOrder = response.existsSameOrder();
                }
            },
            handlesError: (response) => {
                alert("No se pudo crear el pedido.")
            }
        });

        const deliveryDateString = `${deliveryDate.year()}-${deliveryDate.month()+1}-${deliveryDate.date()}`;
        const deliverySite = this.getDeliverySiteWithName(deliverySiteString)
        return this._application.apiClient().existsOrderForSelectedDateAndPurchaseOrder(this.getBusinessName(),
                                                                                        deliverySite.deliverySiteCode,
                                                                                        deliveryDateString,
                                                                                        purchaseOrderNumber,
                                                                                        responseHandler);
    }

    canAddProduct() {
        // FIXME: Duplicated code!
        const business = this._application.business();
        const userRoles = this._application.session().user().roles;
        return userRoles.includes(business.marketplace_operational_role()) || userRoles.includes(business.marketplace_general_administrator_role());
    }

}